/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Lock, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'


type AuthLayoutProps = {
  children: React.ReactNode
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation()

  return (
    <div className='relative grid h-svh max-w-none'>
      <div className='container flex items-center pt-16 sm:pt-0'>
        <div className='mx-auto w-full px-4 sm:w-[480px] sm:px-0'>
          <div className='overflow-hidden rounded-2xl border-2 border-border bg-card shadow-[0_25px_60px_-15px_rgba(15,23,42,0.35)] dark:border-white/15 dark:bg-[#0f1520] dark:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.9)]'>
            <div className='flex items-center justify-between bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-3.5 text-white'>
              <div className='flex items-center gap-2'>
                <ShieldCheck className='size-4' />
                <span className='text-sm font-semibold tracking-wide'>
                  {t('Secure access')}
                </span>
              </div>
              <div className='flex items-center gap-1.5'>
                <span className='inline-block size-2 animate-pulse rounded-full bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,0.8)]' />
                <span className='text-[11px] font-medium tracking-wider uppercase opacity-90'>
                  {t('Encrypted')}
                </span>
              </div>
            </div>

            <div className='flex flex-col justify-center space-y-2 px-4 py-8 sm:p-8'>
              {children}
            </div>

            <div className='flex items-center justify-center gap-2 border-t border-blue-500/20 bg-blue-500/10 px-5 py-3 text-xs font-medium text-blue-700 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-300'>
              <Lock className='size-3.5' />
              <span>{t('Your data is protected with 256-bit encryption')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
